import { useState } from 'react';
import { Dialog } from '@/components/ui/dialog';
import { DialogContentWithoutCloseButton } from '@/components/ui/dialog-no-close';
import { Button } from '@/components/ui/button';
import { Service } from "@/types/dashboard";
import { ServicePricing } from '../pricing/ServicePricing';
import { ServiceDetails } from '../service-details/ServiceDetails';
import { ServiceImageSection } from '../image-handling/ServiceImageSection';
import { useTranslation } from 'react-i18next';

interface ServiceImage {
  id: string;
  service_id: string;
  image_url: string;
  created_at: string;
}

interface ServiceDetailDialogProps {
  service: Service;
  serviceImages: ServiceImage[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequestClick: () => void;
  loadedImages: {[key: string]: boolean};
  handleImageLoad: (imageId: string) => void;
  current: number;
  setCarouselApi: (api: any) => void;
  businessName?: string;
  currency?: string | null;
}

export const ServiceDetailDialog = ({
  service,
  serviceImages,
  open,
  onOpenChange,
  onRequestClick,
  loadedImages,
  handleImageLoad,
  current,
  setCarouselApi,
  businessName,
  currency
}: ServiceDetailDialogProps) => {
  const { t } = useTranslation();
  const hasImages = (serviceImages && serviceImages.length > 0) || (service.image_url && service.image_url.trim() !== '');
  const hasMultipleImages = serviceImages && serviceImages.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentWithoutCloseButton className={`max-h-[90vh] overflow-hidden flex flex-col bg-white border border-gray-200 shadow-lg ${!hasImages ? 'max-w-2xl' : 'max-w-3xl'}`}>
        <div className="flex-1 overflow-y-auto pb-16">
          {hasImages ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative aspect-square overflow-hidden bg-gray-100 rounded-md">
                <ServiceImageSection
                  serviceImages={serviceImages}
                  serviceName={service.name}
                  loadedImages={loadedImages}
                  handleImageLoad={handleImageLoad}
                  current={current}
                  setCarouselApi={setCarouselApi}
                  singleImageUrl={service.image_url || ''}
                  hasMultipleImages={hasMultipleImages}
                  context="customer"
                />
              </div>
              
              <div>
                <ServiceDetails
                  service={service}
                  businessName={businessName}
                  currency={currency}
                  variant="detailed"
                />
                
                <ServicePricing
                  price={service.price}
                  discountedPrice={service.discounted_price}
                  travelFee={service.travel_fee}
                  currency={currency}
                  serviceMode={service.service_mode}
                  variant="detailed"
                />
              </div>
            </div>
          ) : (
            <div>
              <ServiceDetails
                service={service}
                businessName={businessName}
                currency={currency}
                variant="detailed"
              />
              
              <ServicePricing
                price={service.price}
                discountedPrice={service.discounted_price}
                travelFee={service.travel_fee}
                currency={currency}
                serviceMode={service.service_mode}
                variant="detailed"
              />
            </div>
          )}
        </div>
        
        <div className="flex flex-col gap-2 sticky bottom-0 left-0 right-0 mt-auto pt-3 pb-1 px-6 border-t bg-white z-10">
          <Button 
            className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 text-white"
            onClick={onRequestClick}
          >
            {t('servicePage.requestThisService')}
          </Button>
          <Button 
            variant="outline"
            className="w-full"
            onClick={() => onOpenChange(false)}
          >
            {t('servicePage.close')}
          </Button>
        </div>
      </DialogContentWithoutCloseButton>
    </Dialog>
  );
};
